import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { workspaceExtras } from '@/lib/workspace-extras';
import { useCurrentUser } from '@/hooks/useAuth';
import { useDialogs } from '@/components/app/DialogProvider';
import { AppShell } from '@/components/app/AppShell';
import { AdminSiblingNav } from '@/components/app/AdminSiblingNav';
import { JpDate } from '@/components/jp/JpDate';

const ROLE_TONES = {
  owner: 'bg-accent-50 text-accent-700 border-accent-200',
  admin: 'bg-citation-50 text-citation-700 border-citation-200',
  member: 'bg-ink-100 text-ink-700 border-ink-200',
} as const;

export function PeoplePage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { confirm, alert } = useDialogs();
  const { data: me } = useCurrentUser();

  const { data: people = [], isLoading, isError } = useQuery({
    queryKey: ['people', wsId],
    queryFn: () => workspaceExtras.people(wsId!),
    enabled: !!wsId,
  });

  const myRole = people.find((p) => p.userId === me?.id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');

  const refresh = () => qc.invalidateQueries({ queryKey: ['people', wsId] });

  const invite = useMutation({
    mutationFn: () => workspaceExtras.invitePerson(wsId!, { email: email.trim(), role: inviteRole }),
    onSuccess: () => { setEmail(''); refresh(); },
    onError: () =>
      alert({
        title: t('people.invite_error', { defaultValue: 'Could not invite. Check the email and your seat limit.' }) as string,
      }),
  });

  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role: 'admin' | 'member' }) => workspaceExtras.setPersonRole(wsId!, v.userId, v.role),
    onSuccess: refresh,
    onError: () => alert({ title: t('people.role_change_error', { defaultValue: 'Could not change role.' }) as string }),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => workspaceExtras.removePerson(wsId!, userId),
    onSuccess: refresh,
    onError: () => alert({ title: t('people.remove_error', { defaultValue: 'Could not remove member.' }) as string }),
  });

  const onRemove = async (userId: string) => {
    const ok = await confirm({
      title: t('people.remove_confirm_title', { defaultValue: 'Remove member?' }) as string,
      description: t('people.remove_confirm_body', { defaultValue: "They will lose access to this company's workspaces." }) as string,
      destructive: true,
      confirmLabel: t('people.remove', { defaultValue: 'Remove' }) as string,
    });
    if (ok) remove.mutate(userId);
  };

  return (
    <AppShell
      title={t('people.title', { defaultValue: 'People' })}
      description={t('people.subtitle', {
        defaultValue: 'Members of this company — invited, OAuth-linked, and SCIM-provisioned.',
      }) as string}
      actions={
        <Link to={`/app/workspace/${wsId}/scim`} className="btn-ghost px-3.5 py-2 text-sm">
          {t('people.manage_scim', { defaultValue: 'SCIM tokens' })}
        </Link>
      }
      maxWidth="max-w-5xl"
    >
      <AdminSiblingNav wsId={wsId!} current="people" />

      {canManage && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate(); }}
          className="card-paper p-4 mb-4 flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">
              {t('people.invite_title', { defaultValue: 'Invite a teammate' })}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('people.invite_email_placeholder', { defaultValue: 'teammate@company.com' }) as string}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
          </div>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            aria-label={t('people.th_role', { defaultValue: 'Role' }) as string}
          >
            <option value="member">{t('people.invite_role_member', { defaultValue: 'Member' })}</option>
            <option value="admin">{t('people.invite_role_admin', { defaultValue: 'Admin' })}</option>
          </select>
          <button type="submit" disabled={invite.isPending || !email.trim()} className="btn-press px-4 py-2 text-sm">
            {t('people.invite_button', { defaultValue: 'Send invite' })}
          </button>
        </form>
      )}

      {isLoading ? (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
      ) : isError ? (
        <div className="card-paper p-10 text-center text-warn-600 text-sm">
          {t('common.load_error', { defaultValue: 'Could not load. Please try again.' })}
        </div>
      ) : people.length === 0 ? (
        <div className="card-paper p-12 text-center text-ink-500 text-sm">
          {t('people.empty', { defaultValue: 'No members yet.' })}
        </div>
      ) : (
        <div className="card-paper overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100 text-[11px] uppercase tracking-[0.12em]">
                <th className="py-2.5 pl-5 pr-3 font-medium">{t('people.th_person')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.th_role')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.th_provisioning')}</th>
                <th className="py-2.5 px-3 font-medium">{t('people.th_joined')}</th>
                {canManage && <th className="py-2.5 px-3 pr-5 font-medium text-right">{t('people.th_actions', { defaultValue: 'Actions' })}</th>}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const initials = ((p.name || p.email || '?').trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join('') || '?').toUpperCase();
                const isSelf = p.userId === me?.id;
                const targetable = canManage && p.role !== 'owner' && !isSelf;
                return (
                  <tr key={p.userId} className="border-b border-ink-50 align-top hover:bg-paper-100/40">
                    <td className="py-3 pl-5 pr-3">
                      <div className="flex items-center gap-3">
                        {p.pictureUrl ? (
                          <img src={p.pictureUrl} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-white text-[11px] font-semibold">
                            {initials}
                          </span>
                        )}
                        <div>
                          <div className="text-ink-900 font-medium leading-tight">
                            {p.name || '—'}
                            {isSelf && <span className="ml-1.5 text-[11px] text-ink-400">({t('people.you', { defaultValue: 'You' })})</span>}
                          </div>
                          <div className="text-[12px] text-ink-500 num">{p.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`rounded-md text-[10.5px] px-1.5 py-0.5 border font-semibold uppercase tracking-[0.1em] ${ROLE_TONES[p.role]}`}>
                        {p.role}
                      </span>
                      {p.isSiteAdmin && (
                        <span className="ml-1.5 chip-citation text-[10px]">{t('people.site_admin', { defaultValue: 'site admin' })}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-[12px] text-ink-600">
                      {p.scimProvisioned
                        ? <span className="text-accent-700 font-semibold">SCIM</span>
                        : <span className="text-ink-400">OAuth / invite</span>}
                    </td>
                    <td className="py-3 px-3 num text-[12px] text-ink-600 whitespace-nowrap">
                      <JpDate value={p.joinedAt} />
                    </td>
                    {canManage && (
                      <td className="py-3 px-3 pr-5 text-right whitespace-nowrap">
                        {targetable ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => changeRole.mutate({ userId: p.userId, role: p.role === 'admin' ? 'member' : 'admin' })}
                              disabled={changeRole.isPending}
                              className="text-[12px] text-accent-600 hover:underline"
                            >
                              {p.role === 'admin'
                                ? t('people.make_member', { defaultValue: 'Make member' })
                                : t('people.make_admin', { defaultValue: 'Make admin' })}
                            </button>
                            <button
                              onClick={() => onRemove(p.userId)}
                              disabled={remove.isPending}
                              className="text-[12px] text-rose-600 hover:underline"
                            >
                              {t('people.remove', { defaultValue: 'Remove' })}
                            </button>
                          </div>
                        ) : (
                          <span className="text-[12px] text-ink-300">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-5 py-3 text-[11px] text-ink-500 border-t border-ink-100">
            {t('people.showing', {
              defaultValue: 'Showing {{n}} {{noun}}',
              n: people.length,
              noun: people.length === 1 ? 'person' : 'people',
            })}
          </div>
        </div>
      )}
    </AppShell>
  );
}
